// ======================================================================
// 📁 File: src/modules/ipe/enquiry/pages/CreateEnquiryPage.js
// ======================================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ipeEnquiryService from "../../services/ipeEnquiryService";
import axiosClient from "../../../../api/axiosClient";

export default function CreateEnquiryPage() {

  const navigate = useNavigate();

  const [form, setForm] = useState({
    enquiry_no: "",
    enquiry_type_code: "",
    business_partner_id: "",
    customer_name: "",
    project_reference: "",
    priority_code: "",
    industry_code: "",
    application_type_code: "",
    enquiry_date: "",
    expected_response_date: "",
    plant_capacity: "",
    project_location: "",
    remarks: ""
  });

  const [lookups, setLookups] = useState({
    enquiryType: [],
    priority: [],
    industry: [],
    application: []
  });

  const [customerOptions, setCustomerOptions] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ==========================================================
  // LOAD LOOKUPS + DEFAULT VALUES
  // ==========================================================
  useEffect(() => {
    loadLookups();

    const now = new Date();

    const formattedDateTime =
      now.getFullYear() +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0") +
      "T" +
      String(now.getHours()).padStart(2, "0") +
      ":" +
      String(now.getMinutes()).padStart(2, "0");

    const base =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");

    const enquiryNo = `${base}-1`;

    setForm(prev => ({
      ...prev,
      enquiry_date: formattedDateTime,
      enquiry_no: enquiryNo
    }));

  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".customer-dropdown-wrapper")) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadLookups = async () => {
    try {
      const res = await axiosClient.get("/api/ppe/masters/lookup", {
        params: {
          "types[]": [
            "ENQUIRY_TYPE",
            "FMS_PRIORITY",
            "INDUSTRY",
            "APPLICATION_TYPE"
          ]
        }
      });

      const data = res.data?.data || {};

      setLookups({
        enquiryType: data.ENQUIRY_TYPE || [],
        priority: data.FMS_PRIORITY || [],
        industry: data.INDUSTRY || [],
        application: data.APPLICATION_TYPE || []
      });

    } catch (e) {
      console.error("Lookup load failed", e);
    }
  };

  // ==========================================================
  // CUSTOMER SEARCH
  // ==========================================================
  const searchCustomers = async (text) => {
    if (!text || text.length < 2) {
      setCustomerOptions([]);
      return;
    }

    setCustomerLoading(true);
    setShowCustomerDropdown(true);

    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/business-partner/customers/lookup",
        { params: { search: text } }
      );

      setCustomerOptions(res.data?.data?.items || []);
    } catch (e) {
      console.error("Customer search failed", e);
    } finally {
      setCustomerLoading(false);
    }
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================
  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.enquiry_no || !form.business_partner_id || !form.enquiry_date) {
      setError("Required fields missing");
      return;
    }

    setSaving(true);

    try {
      const res = await ipeEnquiryService.createEnquiry(form);
      navigate(`/ipe/enquiries/${res.id}`);
    } catch (err) {
      setError(err?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-3">

      {/* HEADER */}
      <div
        style={{
          background: "linear-gradient(90deg, #6a11cb, #2575fc)",
          borderRadius: "8px",
          padding: "12px 16px",
          color: "white",
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div>
          <h5 style={{ margin: 0 }}>Create Enquiry</h5>
          <small>Status: Draft</small>
        </div>

        <button
          className="btn btn-light btn-sm"
          onClick={() => navigate("/ipe/enquiries")}
        >
          ← Back
        </button>
      </div>

      <div className="card shadow-sm p-4">

        <form onSubmit={onSubmit}>

          {/* BASIC INFO */}
          <h6 className="mb-2 fw-bold">Basic Information</h6>
          <hr />

          <div className="row g-3">

          {/* ROW 1 */}
          <div className="col-md-3">
            <label className="form-label fw-semibold">Enquiry No *</label>
            <input className="form-control" value={form.enquiry_no} readOnly />
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold">Enquiry Type</label>
            <select className="form-select"
              value={form.enquiry_type_code}
              onChange={(e) => onChange("enquiry_type_code", e.target.value)}>
              <option value="">-- Select --</option>
              {lookups.enquiryType.map(l => (
                <option key={l.id} value={l.lookup_code}>{l.lookup_name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold">Priority</label>
            <select className="form-select"
              value={form.priority_code}
              onChange={(e) => onChange("priority_code", e.target.value)}>
              <option value="">-- Select --</option>
              {lookups.priority.map(l => (
                <option key={l.id} value={l.lookup_code}>{l.lookup_name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold">Enquiry Date *</label>
            <input type="datetime-local" className="form-control"
              value={form.enquiry_date} readOnly />
          </div>

          {/* ROW 2 */}
          <div className="col-md-8 customer-dropdown-wrapper position-relative">
            <label className="form-label fw-semibold">Customer *</label>
            <input className="form-control"
              placeholder="Search customer..."
              value={form.customer_name}
              onChange={(e) => {
                onChange("customer_name", e.target.value);
                onChange("business_partner_id", "");
                searchCustomers(e.target.value);
              }}
            />

            {showCustomerDropdown && (
              <div className="border bg-white w-100 shadow-sm"
                style={{ position: "absolute", zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}>
                {customerLoading && <div className="p-2 text-muted">Loading...</div>}
                {!customerLoading && customerOptions.length === 0 && <div className="p-2 text-muted">No customers found</div>}
                {!customerLoading && customerOptions.map(c => (
                  <div key={c.id} className="p-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      onChange("customer_name", c.bp_name);
                      onChange("business_partner_id", c.id);
                      setShowCustomerDropdown(false);
                      setCustomerOptions([]);
                    }}>
                    {c.bp_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">Industry</label>
            <select className="form-select"
              value={form.industry_code}
              onChange={(e) => onChange("industry_code", e.target.value)}>
              <option value="">-- Select --</option>
              {lookups.industry.map(l => (
                <option key={l.id} value={l.lookup_code}>{l.lookup_name}</option>
              ))}
            </select>
          </div>

          {/* ROW 3 */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">Application Type</label>
            <select className="form-select"
              value={form.application_type_code}
              onChange={(e) => onChange("application_type_code", e.target.value)}>
              <option value="">-- Select --</option>
              {lookups.application.map(l => (
                <option key={l.id} value={l.lookup_code}>{l.lookup_name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Expected Response</label>
            <input type="date" className="form-control"
              value={form.expected_response_date}
              onChange={(e) => onChange("expected_response_date", e.target.value)}
            />
          </div>

          {/* ROW 4 */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">Project Reference</label>
            <input className="form-control"
              value={form.project_reference}
              onChange={(e) => onChange("project_reference", e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Plant Capacity</label>
            <input className="form-control"
              value={form.plant_capacity}
              onChange={(e) => onChange("plant_capacity", e.target.value)}
            />
          </div>

          {/* ROW 5 */}
          <div className="col-md-12">
            <label className="form-label fw-semibold">Remarks</label>
            <textarea className="form-control" rows="2"
              value={form.remarks}
              onChange={(e) => onChange("remarks", e.target.value)}
            />
          </div>

        </div>

          {error && <div className="text-danger mt-3">{error}</div>}

          <div className="d-flex justify-content-end mt-4 gap-2">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate("/ipe/enquiries")}
            >
              Cancel
            </button>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Enquiry"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}